/**
 * Reconciliation Page
 *
 * List and manage bank reconciliation sessions.
 * Allows creating new sessions and accessing existing ones.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Card, Button, Modal } from '../components/ui';
import { useAccountStore } from '../store/accountStore';
import { useAuthStore } from '../store/authStore';
import { reconciliationService } from '../services/reconciliation.service';
import { formatDate } from '../utils/formatters';
import type { ReconciliationSession } from '@budgira/shared';

// Status badge colors
const STATUS_COLORS = {
  in_progress: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  completed: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  cancelled: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400',
};

const ReconciliationPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { accounts, fetchAccounts } = useAccountStore();
  const { canManageReconciliation } = useAuthStore();

  // Filter accounts to only show those with write access
  // For reconciliation, users need write access to create/modify sessions
  const writableAccounts = accounts.filter(
    (account) => !account.isShared || account.permission === 'write' || account.permission === 'owner'
  );

  // State
  const [sessions, setSessions] = useState<ReconciliationSession[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [includeCompleted, setIncludeCompleted] = useState(true);

  // New session modal
  const [showNewSessionModal, setShowNewSessionModal] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState<string>('');
  const [sessionName, setSessionName] = useState('');
  const [dateToleranceDays, setDateToleranceDays] = useState(3);
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Delete confirmation modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState<ReconciliationSession | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch sessions
  const fetchSessions = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await reconciliationService.listSessions(includeCompleted);
      setSessions(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch sessions');
    } finally {
      setIsLoading(false);
    }
  }, [includeCompleted]);

  useEffect(() => {
    fetchAccounts();
    fetchSessions();
  }, [fetchAccounts, fetchSessions]);

  // Get account name by ID
  const getAccountName = (accountId: string): string => {
    const account = accounts.find((a) => a.id === accountId);
    return account?.name || accountId;
  };

  // Create new session
  const handleCreateSession = async () => {
    if (!selectedAccountId || !sessionName.trim()) return;

    try {
      setIsCreating(true);
      setCreateError(null);
      const session = await reconciliationService.createSession({
        accountId: selectedAccountId,
        name: sessionName.trim(),
        dateToleranceDays,
      });
      setShowNewSessionModal(false);
      setSessionName('');
      setSelectedAccountId('');
      setDateToleranceDays(3);
      setCreateError(null);
      // Navigate to the session page
      navigate(`/reconciliation/${session.id}`);
    } catch (err: unknown) {
      // Check for SESSION_ALREADY_EXISTS error
      const axiosError = err as { response?: { status?: number; data?: { code?: string; message?: string } }; message?: string };
      if (axiosError.response?.status === 409 || axiosError.response?.data?.code === 'SESSION_ALREADY_EXISTS') {
        setCreateError(t('reconciliation.errors.sessionAlreadyExists', 'Une session de rapprochement est déjà en cours pour ce compte. Terminez-la ou supprimez-la avant d\'en créer une nouvelle.'));
      } else {
        setCreateError(axiosError.response?.data?.message || axiosError.message || 'Failed to create session');
      }
    } finally {
      setIsCreating(false);
    }
  };

  // Delete session
  const handleDeleteSession = async () => {
    if (!sessionToDelete) return;

    try {
      setIsDeleting(true);
      await reconciliationService.deleteSession(sessionToDelete.id);
      setShowDeleteModal(false);
      setSessionToDelete(null);
      fetchSessions();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete session');
    } finally {
      setIsDeleting(false);
    }
  };

  // Calculate progress percentage
  const getProgress = (session: ReconciliationSession): number => {
    if (session.totalLines === 0) return 0;
    const processed = session.matchedLines + session.createdLines + session.ignoredLines;
    return Math.round((processed / session.totalLines) * 100);
  };

  return (
    <div className="container mx-auto px-4 py-6 max-w-6xl">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t('reconciliation.title', 'Rapprochement bancaire')}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {t('reconciliation.description', 'Comparez vos relevés bancaires avec vos opérations')}
          </p>
        </div>
        <Button
          variant="primary"
          onClick={() => {
            setCreateError(null);
            setShowNewSessionModal(true);
          }}
          className={`flex items-center gap-2 ${!canManageReconciliation() ? 'hidden' : ''}`}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          {t('reconciliation.newSession', 'Nouvelle session')}
        </Button>
      </div>

      {/* Error message */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-red-700 dark:text-red-400">{error}</p>
          <button
            onClick={() => setError(null)}
            className="mt-2 text-sm text-red-600 dark:text-red-400 underline"
          >
            {t('common.dismiss', 'Fermer')}
          </button>
        </div>
      )}

      {/* Filter */}
      <div className="mb-4">
        <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
          <input
            type="checkbox"
            checked={includeCompleted}
            onChange={(e) => setIncludeCompleted(e.target.checked)}
            className="rounded border-gray-300 dark:border-gray-600"
          />
          {t('reconciliation.includeCompleted', 'Afficher les sessions terminées')}
        </label>
      </div>

      {/* Sessions list */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      ) : sessions.length === 0 ? (
        <Card className="p-8 text-center">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
            {t('reconciliation.noSessions', 'Aucune session de rapprochement')}
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {t('reconciliation.noSessionsDescription', 'Créez une nouvelle session pour commencer')}
          </p>
          <Button
            variant="primary"
            onClick={() => {
              setCreateError(null);
              setShowNewSessionModal(true);
            }}
            className={`mt-4 ${!canManageReconciliation() ? 'hidden' : ''}`}
          >
            {t('reconciliation.newSession', 'Nouvelle session')}
          </Button>
        </Card>
      ) : (
        <div className="space-y-1">
          {sessions.map((session) => (
            <Card
              key={session.id}
              className="px-3 py-2 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => navigate(`/reconciliation/${session.id}`)}
            >
              {/* Desktop: single row */}
              <div className="hidden sm:flex items-center justify-between gap-2">
                <div className="flex-1 min-w-0 flex items-center gap-3">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {session.name}
                  </h3>
                  <span
                    className={`shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[session.status]}`}
                  >
                    {t(`reconciliation.status.${session.status}`, session.status)}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">{getAccountName(session.accountId)}</span>
                  <span className="text-xs text-gray-400">•</span>
                  <span className="text-xs text-gray-400">{formatDate(session.createdAt)}</span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {session.totalLines > 0 && (
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {getProgress(session)}%
                    </span>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSessionToDelete(session);
                      setShowDeleteModal(true);
                    }}
                    className={`text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 p-1 ${!canManageReconciliation() ? 'hidden' : ''}`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </Button>
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>

              {/* Mobile: 3 rows */}
              <div className="sm:hidden">
                {/* Row 1: Name + Status */}
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {session.name}
                  </h3>
                  <span
                    className={`shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[session.status]}`}
                  >
                    {t(`reconciliation.status.${session.status}`, session.status)}
                  </span>
                  {session.totalLines > 0 && (
                    <span className="ml-auto text-sm font-medium text-gray-700 dark:text-gray-300">
                      {getProgress(session)}%
                    </span>
                  )}
                  <svg className="w-4 h-4 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
                {/* Row 2: Account name */}
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {getAccountName(session.accountId)}
                </div>
                {/* Row 3: Date + Delete */}
                <div className="flex items-center justify-between mt-0.5">
                  <span className="text-xs text-gray-400">{formatDate(session.createdAt)}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSessionToDelete(session);
                      setShowDeleteModal(true);
                    }}
                    className={`text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 p-1 -mr-1 ${!canManageReconciliation() ? 'hidden' : ''}`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* New Session Modal */}
      <Modal
        isOpen={showNewSessionModal}
        onClose={() => setShowNewSessionModal(false)}
        title={t('reconciliation.newSessionTitle', 'Nouvelle session de rapprochement')}
      >
        <div className="space-y-4">
          {/* Error message */}
          {createError && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-700 dark:text-red-400">{createError}</p>
            </div>
          )}

          {/* Account selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('reconciliation.selectAccount', 'Compte à rapprocher')}
            </label>
            <select
              value={selectedAccountId}
              onChange={(e) => setSelectedAccountId(e.target.value)}
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-gray-900 dark:text-white focus:border-primary-500 focus:ring-primary-500"
            >
              <option value="">{t('reconciliation.selectAccountPlaceholder', 'Sélectionner un compte')}</option>
              {writableAccounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.name}
                </option>
              ))}
            </select>
          </div>

          {/* Session name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('reconciliation.sessionName', 'Nom de la session')}
            </label>
            <input
              type="text"
              value={sessionName}
              onChange={(e) => setSessionName(e.target.value)}
              placeholder={t('reconciliation.sessionNamePlaceholder', 'Ex: Relevé Août 2026')}
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-gray-900 dark:text-white focus:border-primary-500 focus:ring-primary-500"
            />
          </div>

          {/* Date tolerance */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('reconciliation.dateTolerance', 'Tolérance de date (jours)')}
            </label>
            <input
              type="number"
              min={0}
              max={30}
              value={dateToleranceDays}
              onChange={(e) => setDateToleranceDays(parseInt(e.target.value) || 0)}
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-gray-900 dark:text-white focus:border-primary-500 focus:ring-primary-500"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {t('reconciliation.dateToleranceHelp', 'Nombre de jours d\'écart accepté pour le rapprochement automatique')}
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 mt-6">
            <Button variant="secondary" onClick={() => setShowNewSessionModal(false)}>
              {t('common.cancel', 'Annuler')}
            </Button>
            <Button
              variant="primary"
              onClick={handleCreateSession}
              disabled={!selectedAccountId || !sessionName.trim() || isCreating}
            >
              {isCreating
                ? t('common.creating', 'Création...')
                : t('reconciliation.createSession', 'Créer la session')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title={t('reconciliation.deleteSessionTitle', 'Supprimer la session')}
      >
        <div className="space-y-4">
          <p className="text-gray-600 dark:text-gray-400">
            {t(
              'reconciliation.deleteSessionConfirm',
              'Êtes-vous sûr de vouloir supprimer la session "{name}" ? Cette action est irréversible.',
              { name: sessionToDelete?.name }
            )}
          </p>
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
              {t('common.cancel', 'Annuler')}
            </Button>
            <Button
              variant="danger"
              onClick={handleDeleteSession}
              disabled={isDeleting}
            >
              {isDeleting ? t('common.deleting', 'Suppression...') : t('common.delete', 'Supprimer')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ReconciliationPage;
